/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Swal from 'sweetalert2'
import { axiosClientAuth } from '../../utils'
import {
  deleteAdminRole,
  getAdminPermissionsCatalog,
  getAdminRoles,
  mapAdminRoleToListRow,
  parseAdminApiErrorMessage,
} from '../../modules/auth/core/_requests'
import { useAuth } from '../../modules/auth'
import ModalEditRole from '../../../_payairo/partials/widgets/_new/modal/ModalEditRole'
import ModalViewRole from '../../../_payairo/partials/widgets/_new/modal/ModalViewRole'
import ModalAddRole from '../../../_payairo/partials/widgets/_new/modal/ModalAddRole'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import { getDashboardPath } from '../../utils/helper'

type RoleRow = {
  uuid: string
  name: string
  description?: string
  created_at?: string
  is_active?: boolean
  available_permissions?: unknown[]
  group_permissions?: unknown[]
  total_users?: number
  /** Present for FastAPI-mapped rows — use DELETE /admin/roles/{id}/ */
  permission_codes?: string[]
}

const RolesManagement: FC = () => {
  const [editRoleShow, setEditRoleShow] = useState(false)
  const [viewRoleShow, setViewRoleShow] = useState(false)
  const [createRoleShow, setCreateRoleShow] = useState(false)
  const [currData, setCurrData] = useState<any>(null)
  const [data, setData] = useState<RoleRow[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [permCatalogCount, setPermCatalogCount] = useState(0)
  const [sortKey, setSortKey] = useState<'name' | 'description' | 'count' | 'created' | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  const { hasPermission } = useAuth()
  const { setRightTolbar } = useLayout()

  const isAdminPermissionUi = true

  const canMutateRoles = useMemo(() => hasPermission('admin.roles.write'), [hasPermission])

  // 🔹 Fetch Roles (FastAPI; fallback to legacy)
  const getData = async () => {
    try {
      const roles = await getAdminRoles()
      setData(roles.map(mapAdminRoleToListRow) as RoleRow[])
    } catch {
      try {
        const res = await axiosClientAuth.get('/auth/api-permission-group')
        setData(res.data?.data || [])
      } catch (error) {
        console.error('Error fetching roles:', error)
      }
    }
  }

  const fetchPermissionCatalogCount = async () => {
    try {
      const powers = await getAdminPermissionsCatalog()
      setPermCatalogCount(powers.length)
    } catch {
      setPermCatalogCount(0)
    }
  }

  const permissionCount = (row: RoleRow) =>
    row.available_permissions?.length ?? row.group_permissions?.length ?? 0

  const filteredSortedRoles = useMemo(() => {
    let list = [...data]
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      list = list.filter((r) => (r.name || '').toLowerCase().includes(q))
    }
    if (statusFilter === 'active') {
      list = list.filter((r) => r.is_active !== false)
    }
    if (statusFilter === 'inactive') {
      list = list.filter((r) => r.is_active === false)
    }
    if (dateFilter === '7') {
      list = list.filter(
        (r) => r.created_at && moment(r.created_at).isAfter(moment().subtract(7, 'days'))
      )
    }
    if (dateFilter === '30') {
      list = list.filter(
        (r) => r.created_at && moment(r.created_at).isAfter(moment().subtract(30, 'days'))
      )
    }
    if (sortKey) {
      list.sort((a, b) => {
        let cmp = 0
        if (sortKey === 'name') {
          cmp = (a.name || '').localeCompare(b.name || '')
        } else if (sortKey === 'description') {
          cmp = (a.description || '').localeCompare(b.description || '')
        } else if (sortKey === 'count') {
          cmp = permissionCount(a) - permissionCount(b)
        } else if (sortKey === 'created') {
          const ta = a.created_at ? moment(a.created_at).valueOf() : 0
          const tb = b.created_at ? moment(b.created_at).valueOf() : 0
          cmp = ta - tb
        }
        return sortAsc ? cmp : -cmp
      })
    }
    return list
  }, [data, searchQuery, statusFilter, dateFilter, sortKey, sortAsc])

  const recentlyCreatedCount = useMemo(() => {
    return data.filter(
      (r) => r.created_at && moment(r.created_at).isAfter(moment().subtract(7, 'days'))
    ).length
  }, [data])

  const toggleSort = (key: 'name' | 'description' | 'count' | 'created') => {
    if (sortKey === key) {
      setSortAsc((s) => !s)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const refreshRolePageData = () => {
    if (isAdminPermissionUi) {
      getData()
      fetchPermissionCatalogCount()
    }
  }

  useEffect(() => {
    if (isAdminPermissionUi) {
      getData()
      fetchPermissionCatalogCount()
    }
  }, [isAdminPermissionUi])


  // 🔹 Delete Role (FastAPI when row has permission_codes; else legacy)
  const deleteRole = (row: RoleRow, onDeleted?: () => void) => {
    Swal.fire({
      titleText: 'Are you sure?',
      text: "This action can't be reverted",
      icon: 'warning',
      confirmButtonText: 'Yes, delete',
      showCancelButton: true,
      cancelButtonText: 'No',
    }).then(async (action) => {
      if (!action.isConfirmed) return
      try {
        if (Array.isArray(row.permission_codes)) {
          await deleteAdminRole(row.uuid)
        } else {
          await axiosClientAuth.delete(`/auth/api-permission-group?uuid=${row.uuid}`)
        }
        await Swal.fire({
          titleText: 'Success!',
          text: 'Role deleted successfully',
          icon: 'success',
          confirmButtonText: 'Ok',
        })
        getData()
        onDeleted?.()
      } catch (error: unknown) {
        console.error('Delete role error:', error)
        const msg =
          error instanceof Error
            ? error.message
            : typeof error === 'object' &&
              error !== null &&
              'response' in error &&
              (error as { response?: { data?: unknown } }).response?.data
              ? parseAdminApiErrorMessage((error as { response: { data: unknown } }).response.data)
              : 'Could not delete role.'
        Swal.fire({ titleText: 'Error', text: msg, icon: 'error', confirmButtonText: 'Ok' })
      }
    })
  }

  return (
    <>
      {/* Roles & Permissions — Admin */}
      {isAdminPermissionUi && (
        <div className='mb-10'>
          <div className='d-flex flex-wrap justify-content-between align-items-start gap-4 mb-8'>
            <div>
              <h1 className='fw-bold fs-2x text-gray-900 mb-2'>Roles &amp; Permissions</h1>
              <p className='text-gray-600 fs-6 mb-0'>
                Manage roles and assign permissions for panel staff.
              </p>
            </div>
            {canMutateRoles && (
              <button
                type='button'
                className='btn btn-primary fw-bold px-6 rounded-3'
                onClick={() => setCreateRoleShow(true)}
              >
                + Create Role
              </button>
            )}
          </div>

          <div className='row g-4 g-xl-5 mb-8'>
            <div className='col-md-4'>
              <div className='card border-0 shadow-sm h-100 rounded-4 overflow-hidden'>
                <div className='card-body d-flex align-items-center gap-4 p-6 bg-light-primary'>
                  <div className='symbol symbol-50px'>
                    <div className='symbol-label bg-primary bg-opacity-25 rounded-3'>
                      <i className='fas fa-shield-alt fs-2 text-primary' />
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-600 fs-7 fw-semibold mb-1'>Total Roles</div>
                    <div className='fs-2 fw-bold text-gray-900'>{data.length}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card border-0 shadow-sm h-100 rounded-4 overflow-hidden'>
                <div className='card-body d-flex align-items-center gap-4 p-6 bg-light-warning'>
                  <div className='symbol symbol-50px'>
                    <div className='symbol-label bg-warning bg-opacity-25 rounded-3'>
                      <i className='fas fa-key fs-2 text-warning' />
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-600 fs-7 fw-semibold mb-1'>Total Permissions</div>
                    <div className='fs-2 fw-bold text-gray-900'>{permCatalogCount}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card border-0 shadow-sm h-100 rounded-4 overflow-hidden'>
                <div className='card-body d-flex align-items-center gap-4 p-6 bg-light-info'>
                  <div className='symbol symbol-50px'>
                    <div className='symbol-label bg-info bg-opacity-25 rounded-3'>
                      <i className='fas fa-history fs-2 text-info' />
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-600 fs-7 fw-semibold mb-1'>Recently Created</div>
                    <div className='fs-2 fw-bold text-gray-900'>{recentlyCreatedCount}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='card border-0 shadow-sm rounded-4 mb-5'>
            <div className='card-body py-4'>
              <div className='row g-3 align-items-center'>
                <div className='col-lg-5'>
                  <div className='position-relative'>
                    <i className='fas fa-search position-absolute top-50 translate-middle-y ms-4 text-gray-500 fs-6' />
                    <input
                      type='search'
                      className='form-control form-control-solid ps-12 rounded-3'
                      placeholder='Search by role name'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className='card border-0 shadow-sm rounded-4'>
            <div className='card-body p-0'>
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4 mb-0'>
                  <thead>
                    <tr className='fw-bold text-gray-600 text-uppercase fs-7 border-bottom border-gray-200 bg-light'>
                      <th className='min-w-175px ps-6 py-4'>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-gray-700 text-hover-primary fw-bold text-uppercase fs-7'
                          onClick={() => toggleSort('name')}
                        >
                          Role Name <i className='fas fa-sort ms-1 fs-7 text-gray-400' />
                        </button>
                      </th>
                      <th className='min-w-200px py-4'>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-gray-700 text-hover-primary fw-bold text-uppercase fs-7'
                          onClick={() => toggleSort('description')}
                        >
                          Description <i className='fas fa-sort ms-1 fs-7 text-gray-400' />
                        </button>
                      </th>
                      <th className='min-w-125px py-4'>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-gray-700 text-hover-primary fw-bold text-uppercase fs-7'
                          onClick={() => toggleSort('count')}
                        >
                          Permissions Count <i className='fas fa-sort ms-1 fs-7 text-gray-400' />
                        </button>
                      </th>
                      <th className='min-w-125px py-4'>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-gray-700 text-hover-primary fw-bold text-uppercase fs-7'
                          onClick={() => toggleSort('created')}
                        >
                          Created At <i className='fas fa-sort ms-1 fs-7 text-gray-400' />
                        </button>
                      </th>
                      <th className='min-w-200px text-end pe-6 py-4'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSortedRoles.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='text-center text-gray-600 py-15'>
                          No roles found.
                        </td>
                      </tr>
                    ) : (
                      filteredSortedRoles.map((ele) => (
                        <tr key={ele.uuid}>
                          <td className='ps-6'>
                            <span className='text-gray-900 fw-bold fs-6'>{ele.name}</span>
                          </td>
                          <td>
                            <span className='text-gray-700 fw-semibold'>
                              {ele.description || '—'}
                            </span>
                          </td>
                          <td>
                            <span className='badge badge-light-primary fs-7 fw-bold'>
                              {permissionCount(ele)}
                            </span>
                          </td>
                          <td>
                            <span className='text-gray-700'>
                              {ele.created_at ? moment(ele.created_at).format('DD MMM YYYY') : '—'}
                            </span>
                          </td>
                          <td className='text-end pe-6'>
                            <button
                              type='button'
                              className='btn btn-sm btn-primary me-2 px-4 rounded-3'
                              onClick={() => {
                                setCurrData(ele)
                                setViewRoleShow(true)
                              }}
                            >
                              View
                            </button>
                            {canMutateRoles && (
                              <>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary border border-primary border-dashed me-2 px-4 rounded-3'
                                  onClick={() => {
                                    setCurrData(ele)
                                    setEditRoleShow(true)
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger border border-danger border-dashed px-4 rounded-3'
                                  onClick={() => deleteRole(ele)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <ModalAddRole
            show={createRoleShow}
            setShow={setCreateRoleShow}
            onSuccess={() => {
              getData()
              fetchPermissionCatalogCount()
            }}
          />
        </div>
      )}

      {isAdminPermissionUi && (
        <ModalEditRole
          show={editRoleShow}
          setShow={setEditRoleShow}
          curr={currData}
          onSuccess={() => {
            getData()
            fetchPermissionCatalogCount()
          }}
          onDelete={
            canMutateRoles && currData
              ? () =>
                deleteRole(currData as RoleRow, () => {
                  setEditRoleShow(false)
                })
              : undefined
          }
        />
      )}
      {isAdminPermissionUi && (
        <ModalViewRole
          show={viewRoleShow}
          setShow={setViewRoleShow}
          curr={currData}
          onDelete={
            canMutateRoles && currData
              ? () =>
                deleteRole(currData as RoleRow, () => {
                  setViewRoleShow(false)
                })
              : undefined
          }
        />
      )}
    </>
  )
}

/* 🔹 Wrapper */
const RolesManagementWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const homePath = getDashboardPath(role, userType)

  const myBreadCrumbs: Array<PageLink> = [
    { title: 'Home', path: homePath, isSeparator: false, isActive: false },
    { title: 'Roles Management', path: '', isSeparator: true, isActive: true },
  ]

  useEffect(() => {
    setRightTolbar('rolesmanagement')
    return () => setRightTolbar('')
  }, [setRightTolbar])

  return (
    <>
      <RolesManagement />
    </>
  )
}

export { RolesManagementWrapper }
